import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import { useNotification } from '../../contexts/NotificationContext';

const AOT_FONT = "'abcNormal','Inter','Arial',sans-serif";
const AOT_FONT_FACE = `
  @font-face {
    font-family: 'abcNormal';
    src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
`;

const formatDate = (dateString) => {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleDateString('es-CL', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return dateString;
  }
};

const formatCurrency = (amount) => new Intl.NumberFormat('es-CL', {
  style: 'currency', currency: 'CLP', minimumFractionDigits: 0
}).format(amount || 0);

const MetricCard = ({ label, value, accent }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
    <div className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">{label}</div>
    <div className="mt-1 text-2xl font-semibold" style={{ color: accent || '#1E293B' }}>{value}</div>
  </div>
);

const STATUS_CONFIG = {
  pending: { label: 'Pendiente', bg: '#FFFBEB', fg: '#B45309' },
  processing: { label: 'En Proceso', bg: 'rgba(0, 74, 173, 0.08)', fg: '#004AAD' },
  completed: { label: 'Completado', bg: '#ECFDF5', fg: '#059669' },
  failed: { label: 'Fallido', bg: '#FEF2F2', fg: '#B91C1C' },
  cancelled: { label: 'Cancelado', bg: '#F1F5F9', fg: '#475569' },
};

const PAYMENT_STATUS_CONFIG = {
  approved: { label: 'Aprobado', bg: '#ECFDF5', fg: '#059669' },
  pending: { label: 'Pendiente', bg: '#FFFBEB', fg: '#B45309' },
  rejected: { label: 'Rechazado', bg: '#FEF2F2', fg: '#B91C1C' },
  in_process: { label: 'En Proceso', bg: 'rgba(0, 74, 173, 0.08)', fg: '#004AAD' },
};

const DESIGN_STATUS_CONFIG = {
  not_started: { label: 'No iniciado', bg: '#F1F5F9', fg: '#475569', icon: '⏳' },
  in_progress: { label: 'En progreso', bg: 'rgba(0, 74, 173, 0.08)', fg: '#004AAD', icon: '🎨' },
  completed: { label: 'Finalizado', bg: '#ECFDF5', fg: '#059669', icon: '✅' },
};

const Badge = ({ config, fallbackLabel }) => (
  <span
    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap"
    style={{ background: config?.bg || '#F1F5F9', color: config?.fg || '#475569' }}
  >
    {config?.icon && <span className="mr-1">{config.icon}</span>}
    {config?.label || fallbackLabel}
  </span>
);

const AdminOrdersTab = () => {
  const { toast, confirm } = useNotification();
  const [metrics, setMetrics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 25, total_pages: 1, has_next: false, has_prev: false });
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);

  const limit = 25;

  const loadMetrics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/orders/metrics`);
      setMetrics(response.data);
    } catch (error) {
      console.error('Error loading order metrics:', error);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;

      const response = await axios.get(`${API}/admin/orders`, { params });
      setOrders(response.data.orders || []);
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, toast]);

  useEffect(() => { loadMetrics(); }, [loadMetrics]);
  useEffect(() => { loadOrders(); }, [loadOrders]);

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const viewOrderDetails = async (orderNumber) => {
    try {
      const response = await axios.get(`${API}/admin/orders/${orderNumber}`);
      setSelectedOrder(response.data);
      setShowOrderModal(true);
    } catch (error) {
      console.error('Error loading order details:', error);
      toast.error('Error al cargar detalles del pedido');
    }
  };

  const updateOrderStatus = async (orderNumber, newStatus) => {
    const ok = await confirm(
      `¿Cambiar el estado del pedido ${orderNumber} a "${STATUS_CONFIG[newStatus]?.label || newStatus}"?`,
      { title: 'Cambiar estado del pedido', confirmLabel: 'Cambiar estado' }
    );
    if (!ok) return;

    try {
      setUpdatingStatus(true);
      await axios.put(`${API}/admin/orders/${orderNumber}/status`, { status: newStatus });

      setOrders((prev) => prev.map((o) => (o.order_number === orderNumber ? { ...o, status: newStatus } : o)));
      if (selectedOrder && selectedOrder.order_number === orderNumber) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
      toast.success('Estado actualizado correctamente');
      loadMetrics();
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Error al actualizar estado');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const resendConfirmationEmail = async (order) => {
    const ok = await confirm(
      `¿Reenviar el correo de confirmación a ${order.customer_email}?`,
      { title: 'Reenviar confirmación', confirmLabel: 'Reenviar' }
    );
    if (!ok) return;

    try {
      setResendingEmail(true);
      const response = await axios.post(`${API}/admin/orders/${order.id}/resend-confirmation`);
      toast.success(response.data?.message || 'Email reenviado correctamente');
    } catch (error) {
      console.error('Error resending confirmation email:', error);
      toast.error(error?.response?.data?.detail || 'Error al reenviar el email');
    } finally {
      setResendingEmail(false);
    }
  };

  const exportToCSV = () => {
    if (orders.length === 0) return;
    const headers = ['Orden', 'Cliente', 'Email', 'Teléfono', 'Producto', 'Cantidad', 'Total', 'Estado', 'Pago', 'Diseño', 'Comuna', 'Región', 'Fecha'];
    const rows = orders.map((o) => [
      o.order_number,
      `${o.customer_name} ${o.customer_lastname}`,
      o.customer_email,
      o.customer_phone || '',
      o.product_name,
      o.quantity,
      o.total_amount,
      STATUS_CONFIG[o.status]?.label || o.status,
      PAYMENT_STATUS_CONFIG[o.payment_status]?.label || o.payment_status || '',
      DESIGN_STATUS_CONFIG[o.design_status]?.label || o.design_status || '',
      o.shipping_comuna || '',
      o.shipping_region || '',
      o.created_at || ''
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `pedidos_pagina${pagination.page}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full space-y-6" data-testid="admin-orders-tab">
      <style>{AOT_FONT_FACE}</style>
      <div>
        <h1 style={{ fontSize: 28, fontWeight: 300, color: '#0A0A0A', letterSpacing: '-0.01em', fontFamily: AOT_FONT }}>Pedidos</h1>
        <p className="text-sm text-[#64748B] mt-1">Seguimiento comercial de todas las compras de tarjetas.</p>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Pedidos totales" value={metrics?.total_orders ?? '—'} />
        <MetricCard
          label="Ingresos totales"
          value={metrics ? formatCurrency(metrics.total_revenue) : '—'}
          accent="#059669"
        />
        <MetricCard label="Ticket promedio" value={metrics ? formatCurrency(metrics.avg_order_value) : '—'} />
        <MetricCard label="Pedidos este mes" value={metrics?.orders_this_month ?? '—'} accent="#004AAD" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard
          label="Ingresos este mes"
          value={metrics ? formatCurrency(metrics.revenue_this_month) : '—'}
          accent="#059669"
        />
        <MetricCard label="Pendientes" value={metrics?.pending_count ?? '—'} accent="#B45309" />
        <MetricCard label="Diseño pendiente" value={metrics?.pending_design_count ?? '—'} accent="#B45309" />
        <MetricCard label="Email sin enviar" value={metrics?.email_pending_count ?? '—'} accent="#B91C1C" />
      </div>

      {/* Filters and Search */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="admin-orders-search"
            placeholder="Buscar por orden, email o comuna"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004AAD]/20 text-sm"
          />
          <select
            data-testid="admin-orders-status-filter"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004AAD]/20 text-sm bg-white"
          >
            <option value="">Todos los estados</option>
            <option value="pending">Pendiente</option>
            <option value="processing">En Proceso</option>
            <option value="completed">Completado</option>
            <option value="failed">Fallido</option>
            <option value="cancelled">Cancelado</option>
          </select>
          <button
            onClick={handleSearch}
            className="px-6 py-2 rounded-lg font-medium text-white transition"
            style={{ background: '#004AAD' }}
          >
            Buscar
          </button>
          <button
            onClick={exportToCSV}
            disabled={orders.length === 0}
            className="px-4 py-2 rounded-lg font-medium text-sm transition disabled:opacity-40"
            style={{ background: '#F1F5F9', color: '#475569' }}
          >
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
        {/* pr-16 keeps the table's visible window clear of the floating
            chat button (fixed bottom-right, ~66px footprint) on mobile,
            where this page's natural height puts the header row right at
            that corner on load, before any scroll — sm:pr-0 removes it
            once there's room to spare on wider screens. */}
        <div className="overflow-x-auto pr-16 sm:pr-0" style={{ WebkitOverflowScrolling: 'touch' }}>
          <table className="w-full text-sm">
            <thead style={{ background: '#F8FAFC' }}>
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Orden</th>
                <th className="text-left px-4 py-3 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Cliente</th>
                <th className="text-left px-4 py-3 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Producto</th>
                <th className="text-left px-4 py-3 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Total</th>
                <th className="text-left px-4 py-3 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Estado</th>
                <th className="text-left px-4 py-3 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Pago</th>
                <th className="text-left px-4 py-3 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Diseño</th>
                <th className="text-left px-4 py-3 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Fecha</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="text-center py-12 text-[#64748B]">Cargando…</td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan="9" className="text-center py-12 text-[#64748B]">No hay pedidos disponibles.</td></tr>
              ) : orders.map((order) => (
                <tr key={order.order_number} data-testid={`admin-order-row-${order.order_number}`} className="border-t border-gray-100 hover:bg-gray-50 transition">
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#1E293B]">{order.order_number}</div>
                    <div className="text-xs text-[#64748B]">{order.shipping_comuna}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#1E293B]">{order.customer_name} {order.customer_lastname}</div>
                    <div className="text-xs text-[#64748B]">{order.customer_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-[#1E293B]">{order.product_name}</div>
                    <div className="text-xs text-[#64748B]">Cantidad: {order.quantity}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="font-semibold text-[#1E293B]">{formatCurrency(order.total_amount)}</div>
                    {order.shipping_cost > 0 && (
                      <div className="text-xs text-[#64748B]">+ {formatCurrency(order.shipping_cost)} envío</div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap"><Badge config={STATUS_CONFIG[order.status]} fallbackLabel={order.status} /></td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {order.payment_status ? <Badge config={PAYMENT_STATUS_CONFIG[order.payment_status]} fallbackLabel={order.payment_status} /> : '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap"><Badge config={DESIGN_STATUS_CONFIG[order.design_status || 'not_started']} fallbackLabel={order.design_status} /></td>
                  <td className="px-4 py-3 whitespace-nowrap text-[#64748B]">{formatDate(order.created_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      data-testid={`admin-order-view-${order.order_number}`}
                      onClick={() => viewOrderDetails(order.order_number)}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg transition"
                      style={{ background: 'rgba(0, 74, 173, 0.08)', color: '#004AAD' }}
                    >
                      Ver detalles
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-[#64748B]">
            <span>
              Mostrando {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
            </span>
            <div className="flex gap-2">
              <button
                disabled={!pagination.has_prev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition"
              >
                Anterior
              </button>
              <button
                disabled={!pagination.has_next}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8"
          style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(6px)' }}
          onClick={() => setShowOrderModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold text-[#1E293B]">Pedido {selectedOrder.order_number}</h3>
                <p className="text-sm text-[#64748B] mt-0.5">{formatDate(selectedOrder.created_at)}</p>
              </div>
              <button onClick={() => setShowOrderModal(false)} className="text-[#64748B] hover:text-[#1E293B]">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6 overflow-y-auto">
              {/* Status + Payment + Email */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Estado del pedido</label>
                  <select
                    value={selectedOrder.status}
                    onChange={(e) => updateOrderStatus(selectedOrder.order_number, e.target.value)}
                    disabled={updatingStatus}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004AAD]/20 text-sm bg-white"
                  >
                    <option value="pending">Pendiente</option>
                    <option value="processing">En Proceso</option>
                    <option value="completed">Completado</option>
                    <option value="failed">Fallido</option>
                    <option value="cancelled">Cancelado</option>
                  </select>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Estado del pago</p>
                  {selectedOrder.payment_status ? <Badge config={PAYMENT_STATUS_CONFIG[selectedOrder.payment_status]} fallbackLabel={selectedOrder.payment_status} /> : <span className="text-sm text-[#64748B]">No disponible</span>}
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Email de confirmación</p>
                  {selectedOrder.confirmation_email_sent ? (
                    <div>
                      <Badge config={{ bg: '#ECFDF5', fg: '#059669' }} fallbackLabel="Enviado" />
                      <p className="text-xs text-[#64748B] mt-1">{formatDate(selectedOrder.email_sent_at)}</p>
                    </div>
                  ) : (
                    <Badge config={{ bg: '#FEF2F2', fg: '#B91C1C' }} fallbackLabel="No enviado" />
                  )}
                  {selectedOrder.payment_status === 'approved' && selectedOrder.status === 'completed' && (
                    <button
                      onClick={() => resendConfirmationEmail(selectedOrder)}
                      disabled={resendingEmail}
                      className="mt-2 text-xs font-medium underline disabled:opacity-50"
                      style={{ color: '#004AAD' }}
                    >
                      {resendingEmail ? 'Enviando…' : 'Reenviar email'}
                    </button>
                  )}
                </div>
              </div>

              {/* Customer Information */}
              <div className="border-t border-gray-100 pt-6">
                <h4 className="text-sm font-semibold uppercase tracking-wider text-[#64748B] mb-3">Información del cliente</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-[#64748B]">Nombre completo</p>
                    <p className="font-medium text-[#1E293B]">{selectedOrder.customer_name} {selectedOrder.customer_lastname}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B]">Email</p>
                    <p className="font-medium text-[#1E293B]">{selectedOrder.customer_email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B]">Teléfono</p>
                    <p className="font-medium text-[#1E293B]">{selectedOrder.customer_phone}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B]">RUT</p>
                    <p className="font-medium text-[#1E293B]">{selectedOrder.customer_rut}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B]">Acepta novedades por correo</p>
                    <p className="font-medium text-[#1E293B]">{selectedOrder.receive_offers ? 'Sí' : 'No'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B]">Guardó datos para próxima compra</p>
                    <p className="font-medium text-[#1E293B]">{selectedOrder.save_info ? 'Sí' : 'No'}</p>
                  </div>
                </div>
              </div>

              {/* Shipping Information */}
              <div className="border-t border-gray-100 pt-6">
                <h4 className="text-sm font-semibold uppercase tracking-wider text-[#64748B] mb-3">Información de envío</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-1">
                  <p className="font-medium text-[#1E293B]">
                    {selectedOrder.shipping_street} {selectedOrder.shipping_number}
                    {selectedOrder.shipping_apartment && `, ${selectedOrder.shipping_apartment}`}
                  </p>
                  <p className="text-[#475569]">{selectedOrder.shipping_comuna}, {selectedOrder.shipping_region}</p>
                  {selectedOrder.shipping_postal_code && (
                    <p className="text-sm text-[#64748B]">Código Postal: {selectedOrder.shipping_postal_code}</p>
                  )}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-[#64748B]">Tipo de envío</p>
                    <p className="font-medium text-[#1E293B] capitalize">{selectedOrder.shipping_type?.replace('_', ' ')}</p>
                  </div>
                </div>
              </div>

              {/* Product and Pricing */}
              <div className="border-t border-gray-100 pt-6">
                <h4 className="text-sm font-semibold uppercase tracking-wider text-[#64748B] mb-3">Detalles del pedido</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-[#475569]">Producto</span><span className="font-medium text-[#1E293B]">{selectedOrder.product_name}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-[#475569]">Cantidad</span><span className="font-medium text-[#1E293B]">{selectedOrder.quantity} unidad(es)</span></div>
                  <div className="flex justify-between text-sm"><span className="text-[#475569]">Precio unitario</span><span className="font-medium text-[#1E293B]">{formatCurrency(selectedOrder.unit_price)}</span></div>
                  {selectedOrder.discount_code && (
                    <div className="flex justify-between text-sm" style={{ color: '#059669' }}>
                      <span>Descuento ({selectedOrder.discount_code})</span><span className="font-medium">-{selectedOrder.discount_percentage}%</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm border-t border-gray-200 pt-2"><span className="text-[#475569]">Subtotal</span><span className="font-medium text-[#1E293B]">{formatCurrency(selectedOrder.subtotal)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-[#475569]">Envío</span><span className="font-medium text-[#1E293B]">{selectedOrder.shipping_cost === 0 ? 'GRATIS' : formatCurrency(selectedOrder.shipping_cost)}</span></div>
                  <div className="flex justify-between border-t-2 border-gray-300 pt-2"><span className="font-semibold text-[#1E293B]">Total</span><span className="font-bold" style={{ color: '#004AAD' }}>{formatCurrency(selectedOrder.total_amount)}</span></div>
                </div>
              </div>

              {/* Payment Information */}
              {selectedOrder.external_reference && (
                <div className="border-t border-gray-100 pt-6">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-[#64748B] mb-3">Información de pago</h4>
                  <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-[#64748B]">Referencia externa</p>
                      <p className="font-mono text-sm text-[#1E293B]">{selectedOrder.external_reference}</p>
                    </div>
                    {selectedOrder.payment_id && (
                      <div>
                        <p className="text-xs text-[#64748B]">ID de pago (MercadoPago)</p>
                        <p className="font-mono text-sm text-[#1E293B]">{selectedOrder.payment_id}</p>
                      </div>
                    )}
                    {selectedOrder.preference_id && (
                      <div>
                        <p className="text-xs text-[#64748B]">Preference ID</p>
                        <p className="font-mono text-sm text-[#1E293B]">{selectedOrder.preference_id}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-[#64748B]">Última actualización</p>
                      <p className="text-sm text-[#1E293B]">{formatDate(selectedOrder.updated_at)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Design Tracking Section */}
              <div className="border-t border-gray-100 pt-6">
                <h4 className="text-sm font-semibold uppercase tracking-wider text-[#64748B] mb-3">🎨 Tracking de diseño</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#1E293B]">Estado del diseño</span>
                    <Badge config={DESIGN_STATUS_CONFIG[selectedOrder.design_status || 'not_started']} fallbackLabel={selectedOrder.design_status} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t border-gray-200">
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-xs text-[#94A3B8] uppercase mb-1">Compra</p>
                      <p className="text-sm font-semibold text-[#1E293B]">{selectedOrder.paid_at ? formatDate(selectedOrder.paid_at) : formatDate(selectedOrder.created_at)}</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-xs text-[#94A3B8] uppercase mb-1">Diseño iniciado</p>
                      <p className="text-sm font-semibold text-[#1E293B]">{selectedOrder.design_started_at ? formatDate(selectedOrder.design_started_at) : '—'}</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-xs text-[#94A3B8] uppercase mb-1">Diseño finalizado</p>
                      <p className="text-sm font-semibold text-[#1E293B]">{selectedOrder.design_completed_at ? formatDate(selectedOrder.design_completed_at) : '—'}</p>
                    </div>
                  </div>

                  {selectedOrder.activation_codes && selectedOrder.activation_codes.length > 0 && (
                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-xs text-[#64748B] mb-2">Códigos de activación generados</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedOrder.activation_codes.map((code, idx) => (
                          <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-mono" style={{ background: '#ECFDF5', color: '#059669' }}>
                            {code}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-[#94A3B8] mt-2">Perfiles habilitados: {selectedOrder.quantity} (= tarjetas compradas)</p>
                    </div>
                  )}

                  {selectedOrder.design_status === 'completed' && (
                    <div className="mt-3 p-3 rounded-lg" style={{ background: '#ECFDF5', border: '1px solid #A7F3D0' }}>
                      <p className="text-sm flex items-center" style={{ color: '#065F46' }}>
                        <span className="mr-2">✅</span>
                        <strong>Listo para producción:</strong>&nbsp;Este pedido puede ser enviado a imprenta.
                      </p>
                    </div>
                  )}

                  {selectedOrder.design_status === 'completed' && (
                    <div className="pt-4 border-t border-gray-200">
                      <a
                        href={`${API}/design/pdf/${selectedOrder.order_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 rounded-lg font-medium text-white transition"
                        style={{ background: '#004AAD' }}
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Descargar PDF del Diseño
                      </a>
                      <p className="text-xs text-[#94A3B8] mt-2">PDF listo para enviar a imprenta (85mm × 55mm)</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setShowOrderModal(false)}
                className="px-5 py-2 rounded-lg font-medium text-[#64748B] hover:bg-gray-100 transition"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrdersTab;
